<template>
  <BaseModal @close="onModalClose">
    <template #title>
      Booking - {{ booking?.bookingRef || "" }}

      <BookingStatusChip
        v-if="booking"
        :status="booking?.status"
        class="ml-3"
      />

      <span
        v-if="booking?.onDisallowList"
        class="bg-red text-red ml-3 inline-flex items-center gap-1 rounded-full bg-opacity-20 px-3 py-1 align-middle text-xs font-medium"
        :title="disallowTitle"
      >
        <IconHeroiconsSolidNoSymbol class="h-3.5 w-3.5" aria-hidden="true" />
        Disallow List
      </span>

      <span
        v-if="booking?.isCompanion"
        class="bg-primary text-primary-fg ml-3 rounded-full px-3 py-1 text-xs font-medium"
      >
        Companion
      </span>
    </template>

    <div class="mb-8 block">
      <div class="border-b border-gray-200">
        <nav class="-mb-px flex space-x-8" aria-label="Tabs">
          <template v-for="tab in tabs" :key="tab">
            <a
              :class="[
                tab === activeTab
                  ? 'border-primary '
                  : 'text-background-fg/50 hover:border-background-fg/30 hover:text-background-fg/70 border-transparent',
                'relative cursor-pointer whitespace-nowrap border-b-2 px-2 pb-4 pt-4 text-sm font-medium',
              ]"
              :aria-current="tab === 'activeTab' ? 'page' : undefined"
              @click="setTab(tab)"
            >
              <template v-if="tab === 'Alerts' && booking?.hasAlert">
                <div class="absolute right-0 top-0">
                  <IconHeroiconsSolidExclamationCircle class="text-error" />
                </div>
              </template>
              {{ tab }}
            </a>
          </template>
        </nav>
      </div>
    </div>

    <template v-if="activeTab === 'Details' && booking">
      <!-- A storage booking's form waits for its location and PIN. -->
      <BookingForm
        v-if="!isCompoundStorage || storageBooking"
        :initial-data="booking"
        :storage-booking="storageBooking"
        @close="onModalClose"
      />
    </template>

    <template v-else-if="activeTab === 'Entry Log'">
      <!-- A storage booking's compound gate crossings sit beside its ANPR
           reads, as keypad entries do on the Entry Log page. -->
      <BaseSegmentedControl
        v-if="entryLogOptions.length > 1"
        v-model="entryLogTab"
        :options="entryLogOptions"
        class="mb-4"
      />
      <StorageCrossingsTable
        v-if="entryLogTab === 'storage'"
        v-model:date-range="entryLogDateRange"
        :filters="cameraEventsFilters"
      />
      <CameraEventsTable
        v-else
        v-model:date-range="entryLogDateRange"
        :filters="cameraEventsFilters"
      />
    </template>

    <template v-else-if="activeTab === 'Actions' && booking">
      <template v-if="isCompoundStorage">
        <StorageBookingActions
          v-if="storageBooking"
          :booking="storageBooking"
        />
      </template>
      <BookingActions v-else :booking="booking" />
    </template>

    <template v-else-if="activeTab === 'Message Log' && booking">
      <BookingMessageLog
        :booking-id="booking.id"
        :is-companion="booking.isCompanion"
      />
    </template>

    <template v-else-if="activeTab === 'Alerts' && booking">
      <BookingAlerts :booking-id="booking.id" />
    </template>

    <template v-if="isBookingLoading || isStorageLoading">
      <BaseContainerLoading tabindex="-1" />
    </template>
  </BaseModal>
</template>

<script setup lang="ts">
import BaseModal from "./BaseModal.vue";
import { useStorageBooking } from "@/services/storage/retrieveStorageBooking";

const props = defineProps({
  bookingId: {
    type: String,
    required: true,
  },
});
const emit = defineEmits<{ (event: "close"): void }>();
const onModalClose = () => emit("close");
const { data: booking, isFetching: isBookingLoading } = useBooking(
  toRef(props, "bookingId"),
);

// Which of its vehicles are on the Disallow List, for the header chip.
const disallowTitle = computed(() => {
  const registrations = booking.value
    ? [booking.value.primaryVehicleBooking, ...booking.value.vehicleBookings]
        .filter((vehicle) => vehicle?.onDisallowList)
        .map((vehicle) => vehicle!.registration)
    : [];
  return registrations.length
    ? `The barrier won't open for ${registrations.join(", ")}`
    : "The barrier won't open for a vehicle on this booking";
});

const activeTab = ref("Details");
const setTab = (tab: string) => {
  activeTab.value = tab;
};

// Entry Log is gated on the booking's own site (not the header selection —
// on "All Sites" every flag reads true). Default to shown while loading so
// the tab doesn't pop in late for digital-arrivals sites.
const { data: sites } = useSites();
const bookingSite = computed(() =>
  sites.value?.find((site) => site.id === booking.value?.siteId),
);
// A storage booking with a compound pitch (Fast Storage): where the unit is
// and the PIN are on Details, its gate crossings in Entry Log, and renew, end,
// cancel and the rest on Actions.
const isCompoundStorage = computed(() => !!booking.value?.storage);
const { data: storageBooking, isFetching: isStorageLoading } =
  useStorageBooking(
    computed(() => (isCompoundStorage.value ? props.bookingId : null)),
  );
const isDigitalArrivals = computed(
  () => bookingSite.value?.isDigitalArrivals ?? true,
);
const tabs = computed(() => [
  "Details",
  ...(isDigitalArrivals.value || isCompoundStorage.value ? ["Entry Log"] : []),
  "Actions",
  "Message Log",
  "Alerts",
]);

const entryLogOptions = computed(() => [
  ...(isDigitalArrivals.value ? [{ label: "ANPR", value: "anpr" }] : []),
  ...(isCompoundStorage.value ? [{ label: "Storage", value: "storage" }] : []),
]);
const entryLogTab = ref<string | null>("anpr");
watchEffect(() => {
  const values = entryLogOptions.value.map(({ value }) => value);
  if (values.length && !values.includes(entryLogTab.value ?? "")) {
    entryLogTab.value = values[0] ?? null;
  }
});

watchEffect(() => {
  if (!tabs.value.includes(activeTab.value)) {
    activeTab.value = "Details";
  }
});

const cameraEventsFilters = reactive({
  bookingId: props.bookingId,
});

// Unbounded, and deliberately local: a booking's entry log should show every
// event recorded against it, not just the range someone last picked on the Entry
// Log page.
const entryLogDateRange = ref<{ start?: Date; end?: Date }>({
  start: undefined,
  end: undefined,
});
</script>
